/**
 * 加法函数
 * @param num1 第一个数字
 * @param num2 第二个数字
 * @returns 两个数字的和
 * @example
 * ```typescript
 * const result = add(0.1, 0.2)
 * console.log(result) // 输出：0.3
 * ```
 */
function add(num1: number, num2: number): number {
  const precision = Math.max(getPrecision(num1), getPrecision(num2))
  const multiplier = 10 ** precision
  return (num1 * multiplier + num2 * multiplier) / multiplier
}

/**
 * 减法函数
 * @param num1 被减数
 * @param num2 减数
 * @returns 两个数字的差
 * @example
 * ```typescript
 * const result = subtract(0.3, 0.1)
 * console.log(result) // 输出：0.2
 * ```
 */
function subtract(num1: number, num2: number): number {
  const precision = Math.max(getPrecision(num1), getPrecision(num2))
  const multiplier = 10 ** precision
  return (num1 * multiplier - num2 * multiplier) / multiplier
}

/**
 * 乘法函数
 * @param num1 第一个数字
 * @param num2 第二个数字
 * @returns 两个数字的乘积
 * @example
 * ```typescript
 * const result = multiply(0.1, 0.2)
 * console.log(result) // 输出：0.02
 * ```
 */
function multiply(num1: number, num2: number): number {
  const precision = getPrecision(num1) + getPrecision(num2)
  const multiplier = 10 ** precision
  return (num1 * multiplier * num2 * multiplier) / (multiplier * multiplier)
}

/**
 * 除法函数
 * @param num1 被除数
 * @param num2 除数
 * @returns 两个数字的商
 * @throws {Error} 当除数为0时抛出错误
 * @example
 * ```typescript
 * const result = divide(0.3, 0.1)
 * console.log(result) // 输出：3
 * ```
 */
function divide(num1: number, num2: number): number {
  if (num2 === 0) {
    throw new Error('除数不能为0')
  }

  const precision = Math.max(getPrecision(num1), getPrecision(num2))
  const multiplier = 10 ** precision
  return (num1 * multiplier) / (num2 * multiplier)
}

/**
 * 获取数字的小数点位数
 * @param num 输入的数字
 * @returns 数字的小数点位数
 */
function getPrecision(num: number): number {
  const numStr = num.toString()
  const decimalIndex = numStr.indexOf('.')
  return decimalIndex === -1 ? 0 : numStr.length - decimalIndex - 1
}

/**
 * 将科学计数法数字转换为普通数字
 * @param num 科学计数法数字
 * @returns 普通数字
 */
function scientificToNumber(num: any): number {
  if (!num)
    return 0
  const strNum = num.toString()
  if (!/e/i.test(strNum)) {
    return num
  }
  return Number(num).toFixed(18).replace(/(\.[0-9]*[1-9])0+$/, '$1').replace(/\.$/, '')
}
/**
 * 数学计算相关的函数
 * @returns 数学计算相关的函数
 * @example
 * ```typescript
 * import { useMath } from '~/math'
 * const { add, subtract, multiply, divide } = useMath()
 * const result = add(0.1, 0.2)
 * console.log(result) // 输出：0.3
 * ```s
 */
export function useMath() {
  return {
    add,
    subtract,
    multiply,
    divide,
    scientificToNumber,
  }
}
